import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
import { ALIGN_OPTIONS, marginFields, SCHEME_COLOR_OPTIONS } from '../_shared/blockFields.js';
import { marginStyle, num, schemeColor, str } from '../_shared/blockRender.js';
import { isThemeColorToken } from '../../theme/colorTokens.js';
import { useProductState } from '../_shared/productContext.js';
import { iconRenders, MaterialIcon, MaterialSymbolsFont } from '../_shared/materialSymbols.js';
/**
 * Shrine `product_price` — config-by-config. The price reads in the ACCENT colour (purple,
 * `price_color: accent-1`) bold; on sale the regular price uses `sale_price_size` and the compare-at is
 * struck (`compare_price_color: text`) at `compare_price_size`. `price_layout` controls the flex
 * direction (price-first vs compare-first, horizontal vs vertical). `displayed_badge` shows the sale badge
 * (its wording is the seller's — `saleBadgeText`, default `-[percentage]` → "-N%", Shrine's own default is
 * `SAVE [percentage]`) or a custom badge; both replace `[percentage]`/`[amount]` from the actual saving and
 * may carry a Material Symbols icon. `badgePlacement` drops the badge on its own line under the price
 * (default — the founder's reference, 2026-09-11) or keeps it on the price line. `price_color` adds a
 * CUSTOM hex option. `letter_spacing` is % of font-size (Shrine `… | divided_by: 100 …em`). Margins(15,15).
 *
 * Shrine has NO responsive variants for price — all settings are uniform across desktop/mobile.
 *
 * DEFERRED (clearly-niche custom-badge icon group — needs an icon/image picker + Material-Symbols font):
 *   icon_scale, icon_1, filled_icon_1, custom_icon_1, icon_2, filled_icon_2, custom_icon_2. We keep the
 *   `[icon_1]`/`[icon_2]` tokens in custom_badge_text but render them as empty (no icon UI yet).
 */
/** Shrine layout values → CSS flex direction + which element comes first. */
const PRICE_LAYOUT_OPTIONS = [
    { value: 'price-first', label: 'Price first' },
    { value: 'price-first-vertical', label: 'Price first (vertical)' },
    { value: 'compare-price-first', label: 'Compare price first' },
    { value: 'compare-price-first-vertical', label: 'Compare price first (vertical)' },
];
/** Shrine sale/custom badge color scheme — note it adds `inverse`, unlike the generic scheme select. */
const BADGE_SCHEME_OPTIONS = [
    { value: 'accent-1', label: 'Accent' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background' },
    { value: 'background-2', label: 'Surface' },
    { value: 'inverse', label: 'Inverse' },
];
/** Price color = the scheme options PLUS a custom hex (same pattern as icon-with-content's color fields). */
const PRICE_COLOR_OPTIONS = [
    ...SCHEME_COLOR_OPTIONS,
    { value: 'custom', label: 'Custom' },
];
/** Badge color = the badge scheme options PLUS a custom hex (background; text contrast is auto). */
const BADGE_COLOR_OPTIONS = [
    ...BADGE_SCHEME_OPTIONS,
    { value: 'custom', label: 'Custom' },
];
/**
 * Font-weight steps for the price + badge. Capped at 800 (Extra bold) because the body font (Inter) is
 * self-hosted up to the 800 face — 900 would silently fall back to 800, so we don't offer a dead step.
 */
const FONT_WEIGHT_OPTIONS = [
    { value: '400', label: 'Regular' },
    { value: '500', label: 'Medium' },
    { value: '600', label: 'Semibold' },
    { value: '700', label: 'Bold' },
    { value: '800', label: 'Extra bold' },
];
/** Alignment select → flex `justify-content` for the price + badge row. */
const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };
/** A readable text color (dark or white) for a given background hex — keeps a custom badge legible. */
function contrastOn(hex) {
    const hex6 = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim())?.[1];
    if (!hex6)
        return '#ffffff';
    const n = parseInt(hex6, 16);
    const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
    return lum > 0.6 ? '#121212' : '#ffffff';
}
/**
 * Read a CSS colour out of a stored ColorValue: custom → its hex; a theme token →
 * `var(--aqx-theme-<token>)` so a re-theme reflows (0019 §4a, allowlisted); else fallback.
 */
function colorHex(v, fallback) {
    if (v && typeof v === 'object') {
        const c = v;
        if (c.ref === 'custom' && typeof c.value === 'string')
            return c.value;
        if (c.ref === 'theme' && typeof c.token === 'string' && isThemeColorToken(c.token))
            return `var(--aqx-theme-${c.token})`;
    }
    if (typeof v === 'string' && v.length > 0)
        return v;
    return fallback;
}
export const priceBlockSchema = {
    type: 'price',
    name: 'Price',
    max: 1,
    fields: [
        {
            id: 'priceLayout',
            type: 'select',
            label: 'Layout',
            default: 'price-first',
            options: PRICE_LAYOUT_OPTIONS,
        },
        {
            id: 'alignment',
            type: 'select',
            label: 'Alignment',
            default: 'left',
            options: ALIGN_OPTIONS,
        },
        {
            id: 'priceColor',
            type: 'select',
            label: 'Price color',
            default: 'accent-1',
            options: PRICE_COLOR_OPTIONS,
        },
        {
            id: 'customPriceColor',
            type: 'color',
            label: 'Custom price color',
            default: { ref: 'custom', value: '#2F5F8A' },
            visibleWhen: { field: 'priceColor', equals: 'custom' },
        },
        {
            id: 'comparePriceColor',
            type: 'select',
            label: 'Compare price color',
            default: 'text',
            options: SCHEME_COLOR_OPTIONS,
        },
        {
            id: 'regularPriceSize',
            type: 'range',
            label: 'Price size',
            info: 'Applies when the product has no compare price.',
            min: 12,
            max: 32,
            step: 1,
            default: 25,
            unit: 'px',
        },
        {
            id: 'salePriceSize',
            type: 'range',
            label: 'Price size with compare price',
            info: 'Applies when the product has a compare price (struck through).',
            min: 12,
            max: 32,
            step: 1,
            default: 20,
            unit: 'px',
        },
        {
            id: 'comparePriceSize',
            type: 'range',
            label: 'Compare price size',
            min: 12,
            max: 32,
            step: 1,
            default: 16,
            unit: 'px',
        },
        {
            id: 'letterSpacing',
            type: 'range',
            label: 'Letter spacing',
            info: 'Relative to font size',
            min: 0,
            max: 20,
            step: 1,
            default: 6,
            unit: '%',
        },
        {
            id: 'priceWeight',
            type: 'select',
            label: 'Price weight',
            // Governs the price AND its struck compare-at. Default Bold (700) = Shrine's price weight; goes up to
            // Extra bold (800) for a heavier look than the old on/off toggle allowed.
            default: '700',
            options: FONT_WEIGHT_OPTIONS,
        },
        // ── Badge group ──
        {
            id: 'displayedBadge',
            type: 'select',
            label: 'Displayed badge',
            group: 'Badge',
            info: 'If variant sold out, the sold-out badge displays instead. "Sale badge" shows when the product has a compare price. "Custom" displays the badge below, even without a compare price.',
            default: 'sale',
            options: [
                { value: 'none', label: 'Hidden' },
                { value: 'sale', label: 'Sale badge' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'saleBadgeText',
            type: 'text',
            label: 'Sale badge text',
            group: 'Badge',
            info: 'Dynamic tokens: [percentage] (% saved), [amount] (money saved). E.g. "SAVE [percentage]".',
            default: '-[percentage]',
            visibleWhen: { field: 'displayedBadge', equals: 'sale' },
        },
        {
            id: 'saleBadgeColor',
            type: 'select',
            label: 'Badge color',
            group: 'Badge',
            default: 'accent-2',
            options: BADGE_COLOR_OPTIONS,
            visibleWhen: { field: 'displayedBadge', notEquals: 'none' },
        },
        {
            id: 'customBadgeColor',
            type: 'color',
            label: 'Custom badge color',
            group: 'Badge',
            default: { ref: 'custom', value: '#2F5F8A' },
            visibleWhen: { field: 'saleBadgeColor', equals: 'custom' },
        },
        {
            id: 'customBadgeIcon',
            type: 'icon-name',
            label: 'Badge icon',
            group: 'Badge',
            info: 'Optional. Type a Google Material Symbols name (e.g. local_offer, bolt). Leave empty for no icon.',
            placeholder: 'local_offer',
            visibleWhen: { field: 'displayedBadge', notEquals: 'none' },
        },
        {
            id: 'customBadgeText',
            type: 'text',
            label: 'Custom badge text',
            group: 'Badge',
            info: 'Dynamic tokens: [percentage] (% saved), [amount] (money saved).',
            default: 'OFFRE SPÉCIALE',
            defaultKey: 'product.special_offer_banner',
            visibleWhen: { field: 'displayedBadge', equals: 'custom' },
        },
        {
            id: 'badgePlacement',
            type: 'select',
            label: 'Badge position',
            group: 'Badge',
            default: 'new-line',
            options: [
                { value: 'new-line', label: 'On its own line, below the price' },
                { value: 'inline', label: 'On the price line' },
            ],
            visibleWhen: { field: 'displayedBadge', notEquals: 'none' },
        },
        {
            id: 'badgeWeight',
            type: 'select',
            label: 'Badge weight',
            group: 'Badge',
            // Default Bold (700) = Shrine's badge weight; up to Extra bold (800).
            default: '700',
            options: FONT_WEIGHT_OPTIONS,
            visibleWhen: { field: 'displayedBadge', notEquals: 'none' },
        },
        ...marginFields(15, 15),
    ],
};
/** Saving between price and compareAt (same currency). Null when there is no real saving. */
function computeSavings(price, compareAt) {
    if (!compareAt || compareAt.amount <= price.amount)
        return null;
    const diff = compareAt.amount - price.amount;
    return {
        amountMoney: { amount: diff, currency: price.currency },
        percent: Math.round((diff / compareAt.amount) * 100),
    };
}
/** Resolve the badge color value to a {bg,fg} pair: `custom` = a hex bg with auto-contrast text;
 *  `inverse` flips to the text colour; otherwise a scheme token bg + the accent-contrast text. */
function badgeColors(value, customHex) {
    if (value === 'custom') {
        return { bg: customHex, fg: contrastOn(customHex) };
    }
    if (value === 'inverse') {
        return { bg: 'var(--aqx-scheme-text)', fg: 'var(--aqx-scheme-bg)' };
    }
    return { bg: schemeColor(value), fg: 'var(--aqx-theme-accent-contrast, #fff)' };
}
/** Replace Shrine custom-badge tokens. `[icon_1]`/`[icon_2]` → '' (icons deferred). */
function renderCustomBadge(template, savings, formatMoney) {
    const percent = savings ? `${savings.percent}%` : '';
    const amount = savings ? formatMoney(savings.amountMoney) : '';
    return template
        .replace(/\[icon_1\]/g, '')
        .replace(/\[icon_2\]/g, '')
        .replace(/\[percentage\]/g, percent)
        .replace(/\[amount\]/g, amount)
        .replace(/\s{2,}/g, ' ')
        .trim();
}
const PriceBlock = ({ block, context }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    const bs = block.settings;
    const price = ps.product.price;
    const compareAt = ps.product.compareAtPrice ?? null;
    const onSale = !!compareAt && compareAt.amount > price.amount;
    const savings = computeSavings(price, compareAt);
    // Colors. The price color adds a CUSTOM hex option (resolved via colorHex) on top of the scheme tokens.
    const priceColorSel = str(bs.priceColor, 'accent-1');
    const priceColor = priceColorSel === 'custom'
        ? colorHex(bs.customPriceColor, '#2F5F8A')
        : schemeColor(priceColorSel);
    const compareColor = schemeColor(str(bs.comparePriceColor, 'text'));
    // Sizes — Shrine uses sale_price_size for the regular price WHEN on sale, regular_price_size otherwise.
    const regularSize = onSale ? num(bs.salePriceSize, 20) : num(bs.regularPriceSize, 25);
    const compareSize = num(bs.comparePriceSize, 16);
    // letter_spacing is % of font-size (Shrine `… | divided_by: 100 …em`).
    const letterSpacing = `${num(bs.letterSpacing, 6) / 100}em`;
    // Font weights (Regular 400 → Extra bold 800; default Bold 700). The price weight also governs the struck
    // compare-at so the compare-at is never heavier than the price it crosses out. The badge weighs on its own.
    const priceWeight = Number(str(bs.priceWeight, '700')) || 700;
    const badgeWeight = Number(str(bs.badgeWeight, '700')) || 700;
    // Layout → flex direction + element order.
    const layout = str(bs.priceLayout, 'price-first');
    const vertical = layout.endsWith('-vertical');
    const compareFirst = layout.startsWith('compare-price-first');
    const flexDirection = vertical ? 'column' : 'row';
    const align = JUSTIFY[str(bs.alignment, 'left')] ?? 'flex-start';
    // Badge.
    const displayedBadge = str(bs.displayedBadge, 'sale');
    const showBadge = displayedBadge === 'custom' || (displayedBadge === 'sale' && onSale);
    const { bg: badgeBg, fg: badgeFg } = badgeColors(str(bs.saleBadgeColor, 'accent-2'), colorHex(bs.customBadgeColor, '#2F5F8A'));
    const customText = str(bs.customBadgeText, t(context, 'product.special_offer_banner'));
    // Optional Material Symbols icon, for BOTH badge modes (its own clear field, not a text token). Only shown
    // when it resolves to a real glyph — a mistyped name (e.g. "Percent Discount") renders no icon, not text.
    const badgeIcon = showBadge ? str(bs.customBadgeIcon, '').trim() : '';
    const showBadgeIcon = iconRenders(badgeIcon);
    // Sale badge wording is the seller's (`saleBadgeText`, tokens replaced from the COMPUTED saving); the
    // default `-[percentage]` renders "-60%". The custom badge shows even without a compare-at price.
    const saleText = str(bs.saleBadgeText, '-[percentage]');
    const badgeLabel = displayedBadge === 'custom'
        ? renderCustomBadge(customText, savings, context.formatMoney)
        : savings
            ? renderCustomBadge(saleText, savings, context.formatMoney)
            : '';
    // Default = the badge on its own line under the price (wraps as a full-width flex row, aligned like the
    // price). `inline` keeps it on the price line, after the compare-at.
    const badgeOwnLine = str(bs.badgePlacement, 'new-line') !== 'inline';
    const badgeEl = showBadge && (badgeLabel || showBadgeIcon) ? (_jsx(BadgePill, { showBadgeIcon: showBadgeIcon, badgeIcon: badgeIcon, badgeLabel: badgeLabel, badgeWeight: badgeWeight, badgeFg: badgeFg, badgeBg: badgeBg })) : null;
    const priceEl = (_jsx("span", { style: {
            fontSize: `${regularSize}px`,
            fontWeight: priceWeight,
            color: priceColor,
            letterSpacing,
            lineHeight: 1.1,
        }, children: context.formatMoney(price) }));
    const compareEl = onSale ? (_jsx("s", { style: {
            textDecoration: 'line-through',
            color: compareColor,
            // Shrine: the struck compare-at is bold and de-emphasised to 90% (rgba(color,.9)). It follows the
            // price's Bold toggle so it never outweighs the price it crosses out.
            opacity: 0.9,
            fontSize: `${compareSize}px`,
            fontWeight: priceWeight,
            letterSpacing,
            lineHeight: 1.1,
        }, children: context.formatMoney(compareAt) })) : null;
    return (_jsxs("div", { style: {
            display: 'flex',
            alignItems: vertical ? 'flex-start' : 'center',
            justifyContent: align,
            flexWrap: 'wrap',
            columnGap: '10px',
            rowGap: '2px',
            ...marginStyle(bs),
        }, children: [_jsxs("div", { style: {
                    display: 'flex',
                    flexDirection,
                    alignItems: vertical ? 'flex-start' : 'baseline',
                    flexWrap: 'wrap',
                    columnGap: '10px',
                    rowGap: '2px',
                }, children: [compareFirst ? compareEl : priceEl, compareFirst ? priceEl : compareEl] }), badgeOwnLine && badgeEl ? (
            // A full-width flex row of its own, so the badge wraps under the price and keeps its natural width
            // (a flex-basis on the badge itself would stretch its background across the line).
            _jsx("div", { "data-badge-placement": "new-line", style: { flexBasis: '100%', display: 'flex', justifyContent: align }, children: badgeEl })) : (badgeEl)] }));
};
/** The badge pill (label + optional icon), or null when nothing is to be shown. */
function BadgePill({ showBadgeIcon, badgeIcon, badgeLabel, badgeWeight, badgeFg, badgeBg, }) {
    return (_jsxs("span", { style: {
            // Shrine `.badge`: 12px, 700, uppercase, letter-spacing ~1px (.1rem), padding 0 .5em,
            // line-height 1.75em, 6px corner radius; inline-flex so an optional icon sits beside the text.
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3em',
            fontSize: '12px',
            fontWeight: badgeWeight,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: badgeFg,
            background: badgeBg,
            paddingBlock: '0',
            paddingInline: '0.5em',
            borderRadius: 'var(--aqx-theme-radius-button, 6px)',
            lineHeight: 1.75,
            whiteSpace: 'nowrap',
        }, children: [showBadgeIcon ? (_jsxs(_Fragment, { children: [_jsx(MaterialSymbolsFont, {}), _jsx(MaterialIcon, { name: badgeIcon, size: 14, color: badgeFg })] })) : null, badgeLabel ? (
            // `dir="ltr"` on the badge, and only on the badge. "-85%" is a NUMBER EXPRESSION, and the
            // bidi algorithm treats a leading "-" as neutral: dropped into an Arabic line it reorders to
            // "85%-", which reads as a different thing. Isolating the run keeps the sign where it belongs
            // without touching the page's direction — the badge still sits at the reading end.
            _jsx("span", { dir: "ltr", style: { unicodeBidi: 'isolate' }, children: badgeLabel })) : null] }));
}
export const priceBlock = {
    type: 'price',
    schema: priceBlockSchema,
    Component: PriceBlock,
    meta: { label: 'Price' },
};
//# sourceMappingURL=index.js.map